import type { InstallTestsRepository } from "~application/install-tests/ports/install-tests-repository.ts";
import type { SiteRepository } from "~application/sites/ports/site-repository.ts";
import type { Modulator } from "~domain/devices/models/modulators/modulator-model.ts";
import type { InstallTests, SiteInstallTests } from "~domain/install-tests/models/install-test-model.ts";
import type { InstallTestV2V5 } from "~domain/install-tests/models/v2v5/install-test-v2v5-model.ts";
import type { InstallTestVx } from "~domain/install-tests/models/vx/install-test-vx-model.ts";
import type { Site } from "~domain/sites/models/site-model.ts";
import type { BackofficeFindV2v5InstallTestsResponse } from "~infrastructure/api/backoffice/contract/install-tests/responses/backoffice-find-v2v5-install-tests-response.ts";
import type { BackofficeFindV2v5InstallTestsResultsResponse } from "~infrastructure/api/backoffice/contract/install-tests/responses/backoffice-find-v2v5-install-tests-results-response.ts";
import type { BackofficeFindVxInstallTestsResponse } from "~infrastructure/api/backoffice/contract/install-tests/responses/backoffice-find-vx-install-tests-response.ts";
import type { BackofficeApi } from "~infrastructure/api/backoffice/http/backoffice-tsrest-client.ts";

import { EModulatorType } from "~domain/devices/models/modulators/modulator-type-model.ts";
import {
  DomainError,
  DomainErrorCode,
  DomainInvalidArgumentError,
  DomainNotFoundError,
  DomainTransportError,
  DomainUnauthorizedError,
} from "~domain/global/errors/domain-error.ts";

function mapBackofficeV2V5InstallTestToDomain(
  backofficeInstallTest: BackofficeFindV2v5InstallTestsResponse[number],
  backofficeInstallTestResults: BackofficeFindV2v5InstallTestsResultsResponse,
): InstallTestV2V5 {
  return {
    id: backofficeInstallTest.installTestId,
    date: backofficeInstallTest.dateSent,
    modulatorCsId: backofficeInstallTest.modulatorCsId ?? undefined,
    modulatorHwId: backofficeInstallTest.modulatorId,
    pilotHwId: backofficeInstallTest.momboxId,
    siteId: backofficeInstallTest.siteId,
    tester: {
      id: backofficeInstallTest.testerId,
      name: backofficeInstallTest.testerName,
    },
    results: backofficeInstallTestResults.map((testResult) => ({
      id: testResult.id,
      measuredPower: testResult.measuredPower ?? undefined,
      testResult: testResult.testResult,
      testTarget: testResult.testTarget,
    })),
  };
}

function mapBackofficeVxInstallTestsToDomain(
  backofficeInstallTests: BackofficeFindVxInstallTestsResponse,
): Array<InstallTestVx> {
  return backofficeInstallTests.map(mapBackofficeVxInstallTestToDomain);
}

function mapBackofficeVxInstallTestToDomain(
  backofficeInstallTest: BackofficeFindVxInstallTestsResponse[number],
): InstallTestVx {
  return {
    deviceType: backofficeInstallTest.deviceType,
    serialNumber: backofficeInstallTest.serialNumber,
    results: backofficeInstallTest.testResults.map((testResult) => ({
      id: testResult.id,
      isAutoTestAllowed: testResult.isAutoTestAllowed,
      measuredPower: testResult.measuredPower ?? undefined,
      pilotHwId: testResult.gatewayHwId,
      serverReceptionDateTime: testResult.serverReceptionDateTime ?? undefined,
      testEndGatewayDateTime: testResult.testEndGatewayDateTime ?? undefined,
      testResult: testResult.testResult,
      testStartGatewayDateTime: testResult.testStartGatewayDateTime,
    })),
  };
}

export class BackofficeInstallTestsRepository implements InstallTestsRepository {
  constructor(
    private readonly backofficeApi: BackofficeApi,
    private readonly siteRepository: SiteRepository,
  ) {}

  async findInstallTests(modulator: Modulator): Promise<InstallTests> {
    if (modulator.type.kind === EModulatorType.VX) {
      return this.findVxInstallTests(modulator);
    }

    return this.findV2V5InstallTests(modulator);
  }

  async findVxInstallTests(modulator: Modulator): Promise<Extract<InstallTests, { kind: "vx" }>> {
    if (!modulator.siteId) {
      throw new DomainInvalidArgumentError(
        `Unable to search for install tests for modulator (${modulator.hwId}) without a site id`,
      );
    }

    const response = await this.backofficeApi.installTests.findVxInstallTests({
      params: {
        siteId: modulator.siteId,
      },
    });

    if (response.status === 200) {
      const test = response.body.find((t) => t.serialNumber === modulator.serialNumber);
      if (!test) {
        throw new DomainNotFoundError(
          `Unable to found test result with modulator serial number "${modulator.serialNumber}" in the modulator site "${modulator.siteId}"`,
        );
      }

      return {
        kind: "vx",
        ...mapBackofficeVxInstallTestToDomain(test),
      };
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    if (response.status === 403) {
      throw new DomainUnauthorizedError();
    }

    throw new DomainTransportError(`Unexpected error`);
  }

  async findV2V5InstallTests(modulator: Modulator): Promise<Extract<InstallTests, { kind: "v2v5" }>> {
    const response = await this.backofficeApi.installTests.findV2V5InstallTests({
      params: {
        modulatorHwId: modulator.hwId,
      },
    });

    if (response.status === 200) {
      const responses = await Promise.all(
        response.body.map((v2v5Test) =>
          this.backofficeApi.installTests
            .findV2V5InstallTestsResults({
              params: {
                installTestV2V5Id: v2v5Test.installTestId,
              },
            })
            .then((r) => ({
              test: v2v5Test,
              results: r,
            })),
        ),
      );

      return {
        kind: "v2v5",
        tests: responses
          .filter((response) => response.results.status === 200)
          .map((response) =>
            mapBackofficeV2V5InstallTestToDomain(
              response.test,
              // safe cast due to the filter
              response.results.body as BackofficeFindV2v5InstallTestsResultsResponse,
            ),
          ),
      };
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    if (response.status === 403) {
      throw new DomainUnauthorizedError();
    }

    throw new DomainTransportError(`Unexpected error`);
  }

  async findSiteInstallTests(site: Site): Promise<SiteInstallTests> {
    const [vxInstallTests, v2v5InstallTests] = await Promise.all([
      this.findSiteVxInstallTests(site),
      this.findSiteV2v5InstallTests(site),
    ]);

    return {
      v2v5: v2v5InstallTests,
      vx: vxInstallTests,
    };
  }

  private async findSiteVxInstallTests(site: Site): Promise<Array<InstallTestVx>> {
    const response = await this.backofficeApi.installTests.findVxInstallTests({
      params: {
        siteId: site.siteId,
      },
    });

    if (response.status === 200) {
      return mapBackofficeVxInstallTestsToDomain(response.body);
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    if (response.status === 403) {
      throw new DomainUnauthorizedError();
    }

    throw new DomainTransportError(`Unexpected error`);
  }

  private async findSiteV2v5InstallTests(site: Site): Promise<Array<InstallTestV2V5>> {
    const modulators = await this.siteRepository.findSiteModulators(site);
    const v2v5Modulators = modulators.filter((mod) => mod.type.kind === EModulatorType.V2V5);

    const responses = await Promise.all(v2v5Modulators.map((mod) => this.findV2V5InstallTests(mod)));
    return responses
      .flatMap((resp) => resp.tests)
      .sort((a, b) => {
        return b.date.getTime() - a.date.getTime();
      });
  }
}
